"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useRef } from "react";
import { useExperience, useNinetailed } from "@ninetailed/experience.js-next";
import profile from "@/profiles/profileSingleton";
import { PersonalizationExperienceMapper } from "./PersonalizationExperienceMapper";
import { PersonalizationExperienceMapperPricingCards } from "./PersonalizationExperienceMapperPricingCards";
import { formatLayoutThemeToT2ThemeClass } from "@/lib/utils";
export type PersonalizedLayoutSectionProps = {
  item: any;
  index: number;
  typeName: string;
  layoutNiceName: string;
  layoutCount: number;
  dividerNeeded: boolean;
  lang: string;
  /** Server-rendered baseline layout JSX — no layoutIndex import on the client. */
  baselineSlot: React.ReactNode;
  /** Server-rendered variant layout JSX keyed by Contentful variant sys.id. */
  variantSlots: Record<string, React.ReactNode>;
};

/**
 * Client wrapper for personalized layout sections. Variant selection and
 * Ninetailed tracking run here; layout formatters are pre-rendered on the
 * server and passed as serializable JSX slots (see renderPersonalizedLayoutSlots).
 */
export function PersonalizedLayoutSection(t0) {
  const $ = _c(51);
  const {
    item,
    typeName,
    layoutNiceName,
    layoutCount,
    dividerNeeded,
    lang,
    baselineSlot,
    variantSlots
  } = t0;
  const id = item.sys?.id;
  const isPricingCards = typeName === "LayoutPricingCards";
  const containerRef = useRef(null);
  const ninetailed = useNinetailed();
  let t1;
  if ($[0] !== item) {
    t1 = {
      ...item
    };
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== dividerNeeded || $[3] !== id || $[4] !== lang || $[5] !== t1) {
    t2 = {
      id,
      value: t1,
      lang,
      dividerNeeded
    };
    $[2] = dividerNeeded;
    $[3] = id;
    $[4] = lang;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const experienceBaseline = t2;
  let t3;
  bb0: {
    const raw = item?.ntExperiencesCollection?.items;
    if (isPricingCards) {
      let t4;
      if ($[7] !== raw) {
        t4 = raw ?? [];
        $[7] = raw;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      const t5 = String(item?.sys?.id ?? id ?? "");
      let t6;
      if ($[9] !== t4 || $[10] !== t5) {
        t6 = PersonalizationExperienceMapperPricingCards(t4, t5);
        $[9] = t4;
        $[10] = t5;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      t3 = t6;
      break bb0;
    }
    let t4;
    if ($[12] !== raw) {
      t4 = raw || [];
      $[12] = raw;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    const t5 = String(item?.sys?.id ?? id ?? "");
    let t6;
    if ($[14] !== t4 || $[15] !== t5) {
      t6 = PersonalizationExperienceMapper(t4, t5);
      $[14] = t4;
      $[15] = t5;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t3 = t6;
  }
  const mappedExperiences = t3;
  let t4;
  if ($[17] !== experienceBaseline || $[18] !== mappedExperiences) {
    t4 = {
      baseline: experienceBaseline,
      experiences: mappedExperiences
    };
    $[17] = experienceBaseline;
    $[18] = mappedExperiences;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const {
    status,
    hasVariants,
    experience,
    variant,
    variantIndex,
    audience,
    isPersonalized
  } = useExperience(t4);
  const variantId = variant && typeof variant === "object" && "id" in variant ? String((variant as {
    id?: string;
  }).id ?? "") : "";
  const baselineId = id != null ? String(id) : "";
  const isVariantHidden = variant != null && typeof variant === "object" && "hidden" in variant && Boolean((variant as {
    hidden?: boolean;
  }).hidden);
  let t5;
  bb1: {
    if (isVariantHidden) {
      t5 = null;
      break bb1;
    }
    if (status !== "loading" && variantId && variantId !== baselineId && variantSlots[variantId]) {
      t5 = variantSlots[variantId];
      break bb1;
    }
    t5 = baselineSlot;
  }
  const activeSlot = t5;
  const variantValue = (variant as {
    value?: {
      theme?: string;
    };
  })?.value;
  const t6 = variantValue?.theme ?? item?.theme;
  let t7;
  if ($[20] !== t6) {
    t7 = formatLayoutThemeToT2ThemeClass(t6);
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const layoutTheme = t7;
  const sectionClassName = layoutTheme ? `w-full ${layoutTheme}` : "w-full";
  let t8;
  if ($[22] !== audience || $[23] !== baselineId || $[24] !== experience || $[25] !== hasVariants || $[26] !== isVariantHidden || $[27] !== ninetailed || $[28] !== status || $[29] !== variant || $[30] !== variantIndex) {
    t8 = () => {
      const element = containerRef.current;
      if (!element || !hasVariants || status === "loading" || isVariantHidden) {
        return;
      }
      const trackingVariant = isVariantHidden && variant ? {
        ...(variant as object),
        id: `${baselineId}-hidden`
      } : variant;
      ninetailed.observeElement({
        element,
        experience,
        componentType: "Entry",
        audience,
        variant: trackingVariant,
        variantIndex
      }, {});
      return () => {
        ninetailed.unobserveElement(element);
      };
    };
    $[22] = audience;
    $[23] = baselineId;
    $[24] = experience;
    $[25] = hasVariants;
    $[26] = isVariantHidden;
    $[27] = ninetailed;
    $[28] = status;
    $[29] = variant;
    $[30] = variantIndex;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== audience || $[33] !== baselineId || $[34] !== experience || $[35] !== hasVariants || $[36] !== isPersonalized || $[37] !== isVariantHidden || $[38] !== ninetailed || $[39] !== status || $[40] !== variant || $[41] !== variantIndex) {
    t9 = [audience, baselineId, experience, hasVariants, isVariantHidden, isPersonalized, ninetailed, status, variant, variantIndex];
    $[32] = audience;
    $[33] = baselineId;
    $[34] = experience;
    $[35] = hasVariants;
    $[36] = isPersonalized;
    $[37] = isVariantHidden;
    $[38] = ninetailed;
    $[39] = status;
    $[40] = variant;
    $[41] = variantIndex;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  useEffect(t8, t9);
  const t10 = `${layoutNiceName}-${layoutCount}`;
  let t11;
  if ($[43] !== layoutCount || $[44] !== layoutNiceName) {
    t11 = profile.NEXT_PUBLIC_MODE === "live-preview" && <div id={`${layoutNiceName}-${layoutCount}`} className="absolute z-100 bg-white p-2 text-gray-m9">{layoutNiceName}-{layoutCount}</div>;
    $[43] = layoutCount;
    $[44] = layoutNiceName;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== activeSlot || $[47] !== sectionClassName || $[48] !== t10 || $[49] !== t11) {
    t12 = <div ref={containerRef} id={t10} className={sectionClassName}>{t11}{activeSlot}</div>;
    $[46] = activeSlot;
    $[47] = sectionClassName;
    $[48] = t10;
    $[49] = t11;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  return t12;
}